/**
 * ENG-8893 (ADR-0063 slice 2a) — which KIND of principal an OAuth grant is
 * for.
 *
 * The platform already had this axis before this module existed:
 * `NotePrincipal` in the API's meeting-notes lib is
 * `{kind:'user'} | {kind:'agent'}`. Naming the OAuth side after the device
 * ("desktop") instead would have forced a new arm, a new scope and a
 * migration for every future client; naming it after the principal means a
 * mobile app or a human CLI login is the SAME `user` principal with a
 * different `client_id`, and needs none of that.
 *
 * Shared between the API and the webapp because the authorization endpoint
 * lives in the webapp and the token endpoint lives in the API — one rule, two
 * codebases.
 */

/** Stored in `principal_kind` on consent sessions and authorization codes. */
export type OAuthPrincipalKind = 'agent' | 'user';

/**
 * The scope a human client requests to create and read their own meeting
 * notes. Presence of this scope is what routes an authorization request down
 * the user-principal path.
 *
 * Deliberately NOT of the form `mcp:agent:*`: that family is parsed by the
 * authorize endpoint to resolve a specific agent, and a human grant resolves
 * no agent at all.
 */
export const NOTES_WRITE_SCOPE = 'notes:write';

/** Every scope a user-principal grant may carry today. */
export const USER_PRINCIPAL_SCOPES: readonly string[] = [NOTES_WRITE_SCOPE];

/**
 * Does this authorization request name a user principal?
 *
 * Read from the requested scopes, which is the only signal available before a
 * consent session exists. Afterwards, read `principal_kind` from the row —
 * never re-derive the kind from scopes once it has been recorded, or a scope
 * rewrite (the consent screen does one for the agent path) can change the
 * principal underneath the grant.
 */
export function isUserPrincipalScopeRequest(scopes: readonly string[]): boolean {
  // EVERY requested scope must be a user-principal scope, not merely one of
  // them. An any-match rule would route a MIXED request such as
  // `notes:write mcp:agent:*` down the user path, where no agent is resolved —
  // producing a human token that also carries an agent scope nothing checked.
  // Requiring all of them means a mixed request falls through to the agent
  // path, which resolves and validates the agent as it always did.
  //
  // An empty scope list is not a user-principal request: `scopes.every` is
  // vacuously true on an empty array, and a request naming no scope at all is
  // the legacy agent-wildcard default.
  if (scopes.length === 0) return false;
  return scopes.every((s) => USER_PRINCIPAL_SCOPES.includes(s));
}

/**
 * Human-readable permission lines for the consent screen, keyed by scope.
 * The consent screen is the only place a person is told what they are
 * granting, so an unrecognised scope must never render as a raw scope string.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ENG-8933: WHY THIS LINE NAMES ENUMERATION, AND WHY IT IS ONE SCOPE AND NOT TWO
 * ─────────────────────────────────────────────────────────────────────────────
 * The desktop client cannot create a note without knowing which organization to
 * file it in, and cannot deliver one without knowing which destination to send
 * it to. So `GET /notes/organizations` and `GET /notes/destinations` had to
 * become reachable by this token — and the previous wording of this line,
 *
 *     "Create meeting notes as you, and read the notes you own"
 *
 * did not describe reading a list of your organizations or your agents.
 * Serving them under a scope whose label says otherwise is silent widening:
 * the ENG-8806 shape ADR-0063 cites against itself, where a surface copies a
 * sibling's auth posture without the entitlement backstop that made it safe.
 *
 * Three options were considered (recorded on ENG-8933). A second scope —
 * `notes:destinations` — is the most literally honest and was rejected on the
 * grounds that it splits a grant nobody can usefully take half of: a client
 * holding `notes:write` without it can do nothing at all, so the second scope
 * would be a consent checkbox with one legal value. Saying nothing was rejected
 * outright.
 *
 * What remains is to widen the LABEL, which is what happened. The enumeration
 * is constitutive of the action already granted, not adjacent to it, and the
 * user is now told so before they approve. Labels are display-only, so no
 * existing token was invalidated — worth stating because it is also the reason
 * this choice is cheap TODAY and will not be later: consents granted under the
 * old wording are not re-collected, and there is exactly one such token (minted
 * 2026-08-16, by the author of this change). Once real users hold tokens, a
 * change of this shape needs re-consent rather than a re-worded string.
 *
 * The rule this leaves behind: these routes may return an id and a display
 * name, and nothing else. The moment one of them returns agent config, member
 * lists or anything a picker does not draw, this label is wrong again.
 */
export const USER_PRINCIPAL_SCOPE_LABELS: Readonly<Record<string, string>> = {
  [NOTES_WRITE_SCOPE]:
    'Create meeting notes as you, read the notes you own, and see the names of your organizations and agents so you can choose where a note goes',
};
